import { useState } from 'react';
import TranslatableText from './TranslatableText';
import { npcLines } from '../game/lang';
import { nextQuest, QUESTS, type NpcId } from '../game/npcs';
import { speciesById, speciesImage } from '../lib/aquarium';

// ─────────────────────────────────────────────────────────────────────────────
// Talking to the Old Turtle or the Ghost Captain. They speak the class's language
// (tap a word to translate it); their quest asks for something, and the student
// hands it over when they have it (ocean_quest checks and rewards it).
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  npc: NpcId;
  lang: string;
  native: string;
  done: Set<string>;
  inventory: Record<string, number>;
  bucket: { species: string }[];
  itemIcon: (item: string) => string;
  itemName: (item: string) => string;
  onGive: (quest: string) => Promise<void>;
  onClose: () => void;
};

export default function NpcDialog({ npc, lang, native, done, inventory, bucket, itemIcon, itemName, onGive, onClose }: Props) {
  const lines = npcLines(lang);
  const q = nextQuest(npc, done);
  const [busy, setBusy] = useState(false);
  const step = QUESTS[npc].findIndex(x => x.id === q?.id);
  const have = q?.item ? inventory[q.item] ?? 0 : q?.fish ? bucket.filter(f => f.species === q.fish).length : 0;
  const need = q?.item ? q.qty ?? 1 : 1;

  return (
    <div className="ocean-modal" role="dialog" aria-modal="true" aria-label={lines[npc]} onClick={onClose}>
      <div className="ocean-card npc-card" onClick={e => e.stopPropagation()}>
        <div className="row">
          <h3 dir="auto">{npc === 'turtle' ? '🐢' : '👻'} {lines[npc]}</h3>
          <span className="spacer" />
          <button type="button" className="icon" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="npc-speech" dir="auto">
          <TranslatableText text={q ? (step === 0 ? `${npc === 'turtle' ? lines.turtleHi : lines.captainHi} ${lines.quests[q.id]}` : lines.quests[q.id]) : npc === 'turtle' ? lines.turtleDone : lines.captainDone} source={lang} target={native} />
        </div>
        {q ? (
          <>
            <div className="npc-need">
              <span className="small muted">Quest {step + 1} of {QUESTS[npc].length}</span>
              <span className="spacer" />
              {q.fish
                ? <span className="inv-chip"><img src={speciesImage(q.fish)} alt="" /> {Math.min(have, 1)}/1 in your bucket</span>
                : <span className="inv-chip">{itemIcon(q.item!)} {itemName(q.item!)} {Math.min(have, need)}/{need}</span>}
            </div>
            <div className="npc-need">
              <span className="small muted">Reward:</span>
              {Object.entries(q.reward).map(([k, v]) => <span key={k} className="inv-chip small">{itemIcon(k)} {itemName(k)} ×{v}</span>)}
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose}>Later</button>
              <button type="button" className="primary" disabled={busy || have < need}
                onClick={async () => { setBusy(true); try { await onGive(q.id); } finally { setBusy(false); } }}>
                {have < need ? (q.fish ? `Catch a ${speciesById(q.fish).name} first` : 'You need more') : 'Give it'}
              </button>
            </div>
          </>
        ) : <p className="small muted" style={{ margin: 0 }}>You’ve done every quest here. 🎉</p>}
      </div>
    </div>
  );
}
